import { useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { Check, Mic, Users, Calendar } from 'lucide-react';
import SEO from '@/components/SEO';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const TRACKS = {
  speaker: {
    icon: Mic, title: 'Speak', subtitle: "Share your 7-minute story", price: null, priceNote: 'By application',
    benefits: ['7-minute story slot at Edition 001', 'Permanent She Built This profile', 'Pre-dinner invitation', 'Professional photography', 'Video recording of your talk'],
    description: 'Share what you built, what it cost, and what you learned. Speakers are selected based on the significance and verifiability of their achievements.',
  },
  mentor: {
    icon: Users, title: 'Mentor', subtitle: 'Guide the next generation', price: null, priceNote: 'By application',
    benefits: ['3 rotations × 5 minutes each', 'Mentor Directory listing', 'Pre-dinner invitation', 'Networking with speakers and delegates', 'Recognition on shebuiltthis.co'],
    description: 'Share your expertise in structured mentoring sessions. Mentors are selected based on their experience and ability to guide others.',
  },
  delegate: {
    icon: Calendar, title: 'Attend', subtitle: 'Join Edition 001', price: 'AED 1,000', priceNote: 'Full evening access',
    benefits: ['Full evening access to Edition 001', "Three Builders' Stories", 'The Fireside with Dr. Neha Dewan', 'The Commitment Moment', 'Networking reception'],
    description: 'Experience She Built This Edition 001 in person. Limited seats available.',
  },
};

const ParticipatePage = () => {
  const [searchParams] = useSearchParams();
  const [activeTrack, setActiveTrack] = useState(searchParams.get('track') || 'speaker');
  const [formData, setFormData] = useState({ name: '', email: '', company: '', title: '', linkedin: '', sector: '', message: '' });
  const [loading, setLoading] = useState(false);

  const handleInputChange = (e) => setFormData(prev => ({ ...prev, [e.target.name]: e.target.value }));

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      if (activeTrack === 'delegate') {
        const response = await axios.post(`${API_URL}/api/participate/delegate/checkout`, { name: formData.name, email: formData.email, company: formData.company, title: formData.title });
        if (response.data.checkout_url) window.location.href = response.data.checkout_url;
      } else if (activeTrack === 'speaker') {
        await axios.post(`${API_URL}/api/participate/speaker`, { name: formData.name, email: formData.email, company: formData.company, title: formData.title, linkedin: formData.linkedin, message: formData.message });
        toast.success('Speaker application submitted! We will review and respond within 14 days.');
        setFormData({ name: '', email: '', company: '', title: '', linkedin: '', sector: '', message: '' });
      } else if (activeTrack === 'mentor') {
        await axios.post(`${API_URL}/api/participate/mentor`, { name: formData.name, email: formData.email, company: formData.company, title: formData.title, sector: formData.sector, linkedin: formData.linkedin, message: formData.message });
        toast.success('Mentor application submitted! We will review and respond within 14 days.');
        setFormData({ name: '', email: '', company: '', title: '', linkedin: '', sector: '', message: '' });
      }
    } catch (error) {
      console.error('Submission error:', error);
      toast.error('Something went wrong. Please try again or email support@shebuiltthis.com');
    } finally {
      setLoading(false);
    }
  };

  const track = TRACKS[activeTrack];
  const TrackIcon = track.icon;

  return (
    <div data-testid="participate-page" className="min-h-screen bg-[#FAF9F6]">
      <SEO title="Participate — Speak, Mentor, or Attend Edition 001" description="Join She Built This Edition 001 in UAE 2026." url="https://shebuiltthis.com/participate" />

      {/* Hero */}
      <section className="pt-32 pb-16 px-6">
        <div className="max-w-[1000px] mx-auto text-center">
          <p className="text-[10px] tracking-[0.35em] uppercase mb-6" style={{ fontFamily: "'DM Mono', monospace", color: '#C25E3E' }}>EDITION 001</p>
          <h1 className="text-4xl lg:text-[64px] leading-[1.0] mb-6" style={{ fontFamily: "'Cormorant Garamond', serif", fontWeight: 300, color: '#1A1A1A' }}>
            Are you ready to be<br />in the room?
          </h1>
          <p className="text-base mb-2" style={{ fontFamily: "'Lora', serif", color: '#4A4A4A' }}>She Built This Edition 001 · UAE 2026</p>
          <p className="text-[11px] tracking-[0.1em]" style={{ fontFamily: "'DM Mono', monospace", color: '#C25E3E' }}>Venue to be announced</p>
        </div>
      </section>

      {/* Track Selector */}
      <section className="border-t border-b border-[rgba(201,168,76,0.12)]">
        <div className="max-w-[1200px] mx-auto px-6">
          <div className="grid grid-cols-3">
            {Object.entries(TRACKS).map(([key, t]) => {
              const Icon = t.icon;
              const isActive = activeTrack === key;
              return (
                <button key={key} onClick={() => setActiveTrack(key)} data-testid={`track-${key}`}
                  className={`py-8 px-4 text-center transition-all border-b-2 ${isActive ? 'border-[#C25E3E] bg-[#C25E3E]/5' : 'border-transparent hover:bg-[#1A1A1A]/[0.03]'}`}>
                  <Icon size={28} className={`mx-auto mb-3 ${isActive ? 'text-[#C25E3E]' : 'text-[#1A1A1A]/35'}`} />
                  <h3 className={`text-xl mb-1 ${isActive ? 'text-[#1A1A1A]' : 'text-[#1A1A1A]/55'}`} style={{ fontFamily: "'Cormorant Garamond', serif", fontWeight: 600 }}>{t.title}</h3>
                  <p className="text-[10px] tracking-[0.12em]" style={{ fontFamily: "'DM Mono', monospace", color: '#888' }}>{t.subtitle}</p>
                </button>
              );
            })}
          </div>
        </div>
      </section>

      {/* Track Details + Form */}
      <section className="py-20 lg:py-28">
        <div className="max-w-[1200px] mx-auto px-6">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16">
            {/* Left: Info */}
            <div>
              <div className="flex items-center gap-4 mb-8">
                <TrackIcon size={36} className="text-[#C25E3E]" />
                <div>
                  <h2 className="text-4xl" style={{ fontFamily: "'Cormorant Garamond', serif", fontWeight: 400, color: '#1A1A1A' }}>{track.title}</h2>
                  <p className="text-[10px] tracking-[0.12em]" style={{ fontFamily: "'DM Mono', monospace", color: '#888' }}>{track.subtitle}</p>
                </div>
              </div>
              <p className="text-base leading-relaxed mb-8" style={{ fontFamily: "'Lora', serif", color: '#3A3A3A' }}>{track.description}</p>
              <div className="mb-8">
                <span className="text-4xl" style={{ fontFamily: "'Cormorant Garamond', serif", fontWeight: 600, color: '#C25E3E' }}>{track.price || 'Free'}</span>
                <span className="text-[11px] tracking-[0.1em] ml-3" style={{ fontFamily: "'DM Mono', monospace", color: '#888' }}>{track.priceNote}</span>
              </div>
              <p className="text-[10px] tracking-[0.2em] uppercase mb-4" style={{ fontFamily: "'DM Mono', monospace", color: '#C25E3E' }}>WHAT YOU GET</p>
              <ul className="space-y-3">
                {track.benefits.map((b, i) => (
                  <li key={i} className="flex items-start gap-3">
                    <Check size={16} className="text-[#C25E3E] flex-shrink-0 mt-1" />
                    <span className="text-sm" style={{ fontFamily: "'Lora', serif", color: '#3A3A3A' }}>{b}</span>
                  </li>
                ))}
              </ul>
            </div>

            {/* Right: Form */}
            <div className="bg-[#1A1A1A] border border-[rgba(201,168,76,0.12)] p-8 lg:p-10">
              <h3 className="text-2xl mb-6" style={{ fontFamily: "'Cormorant Garamond', serif", fontWeight: 600, color: '#F4F0E6' }}>
                {activeTrack === 'delegate' ? 'Register' : 'Apply'}
              </h3>
              <form onSubmit={handleSubmit} className="space-y-5">
                <div>
                  <label className="text-[10px] tracking-[0.15em] uppercase block mb-2" style={{ fontFamily: "'DM Mono', monospace", color: 'rgba(250,248,244,0.3)' }}>Full Name *</label>
                  <input type="text" name="name" value={formData.name} onChange={handleInputChange} required className="input-field" data-testid="input-name" />
                </div>
                <div>
                  <label className="text-[10px] tracking-[0.15em] uppercase block mb-2" style={{ fontFamily: "'DM Mono', monospace", color: 'rgba(250,248,244,0.3)' }}>Email *</label>
                  <input type="email" name="email" value={formData.email} onChange={handleInputChange} required className="input-field" data-testid="input-email" />
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="text-[10px] tracking-[0.15em] uppercase block mb-2" style={{ fontFamily: "'DM Mono', monospace", color: 'rgba(250,248,244,0.3)' }}>Company</label>
                    <input type="text" name="company" value={formData.company} onChange={handleInputChange} className="input-field" data-testid="input-company" />
                  </div>
                  <div>
                    <label className="text-[10px] tracking-[0.15em] uppercase block mb-2" style={{ fontFamily: "'DM Mono', monospace", color: 'rgba(250,248,244,0.3)' }}>Title</label>
                    <input type="text" name="title" value={formData.title} onChange={handleInputChange} className="input-field" data-testid="input-title" />
                  </div>
                </div>
                {(activeTrack === 'speaker' || activeTrack === 'mentor') && (
                  <>
                    <div>
                      <label className="text-[10px] tracking-[0.15em] uppercase block mb-2" style={{ fontFamily: "'DM Mono', monospace", color: 'rgba(250,248,244,0.3)' }}>LinkedIn Profile</label>
                      <input type="url" name="linkedin" value={formData.linkedin} onChange={handleInputChange} placeholder="https://linkedin.com/in/..." className="input-field" data-testid="input-linkedin" />
                    </div>
                    {activeTrack === 'mentor' && (
                      <div>
                        <label className="text-[10px] tracking-[0.15em] uppercase block mb-2" style={{ fontFamily: "'DM Mono', monospace", color: 'rgba(250,248,244,0.3)' }}>Industry / Sector</label>
                        <input type="text" name="sector" value={formData.sector} onChange={handleInputChange} className="input-field" data-testid="input-sector" />
                      </div>
                    )}
                    <div>
                      <label className="text-[10px] tracking-[0.15em] uppercase block mb-2" style={{ fontFamily: "'DM Mono', monospace", color: 'rgba(250,248,244,0.3)' }}>
                        {activeTrack === 'speaker' ? 'What did you build and what did it cost? *' : 'What expertise can you share?'}
                      </label>
                      <textarea name="message" value={formData.message} onChange={handleInputChange} rows={4} required={activeTrack === 'speaker'} className="input-field resize-none" data-testid="input-message" />
                    </div>
                  </>
                )}
                <button type="submit" disabled={loading} className="w-full h-14 bg-[#C25E3E] text-[#080808] text-[13px] tracking-[0.12em] uppercase transition-all hover:bg-[#C25E3E]/80 disabled:opacity-50" style={{ fontFamily: "'DM Mono', monospace" }} data-testid="submit-button">
                  {loading ? 'Processing...' : activeTrack === 'delegate' ? 'Proceed to Payment' : 'Submit Application'}
                </button>
                <p className="text-[10px] text-center" style={{ fontFamily: "'DM Mono', monospace", color: 'rgba(250,248,244,0.25)' }}>
                  {activeTrack === 'delegate' ? 'You will be redirected to secure payment.' : 'Applications reviewed within 14 days.'}
                </p>
              </form>
            </div>
          </div>
        </div>
      </section>

      {/* What to Expect */}
      <section className="py-24 border-t border-[#1A1A1A]/8">
        <div className="max-w-[900px] mx-auto px-6 text-center">
          <p className="text-[10px] tracking-[0.3em] uppercase mb-6" style={{ fontFamily: "'DM Mono', monospace", color: '#C25E3E' }}>EDITION 001</p>
          <h2 className="text-4xl lg:text-[52px] leading-[1.05] mb-12" style={{ fontFamily: "'Cormorant Garamond', serif", fontWeight: 300, color: '#1A1A1A' }}>What to Expect</h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8 text-left">
            {[
              { t: "Three Builders' Stories", d: '7-minute talks from women who built something real. No corporate polish — honest accounts.' },
              { t: 'The Fireside', d: 'Dr. Neha Dewan explores the research — what fifty years of data actually shows.' },
              { t: 'The Commitment Moment', d: 'Make a specific, documented pledge to action. Not vague resolutions — concrete commitments.' },
            ].map((item) => (
              <div key={item.t} className="border-l-2 border-[#C25E3E] pl-6">
                <h3 className="text-xl mb-2" style={{ fontFamily: "'Cormorant Garamond', serif", fontWeight: 600, color: '#1A1A1A' }}>{item.t}</h3>
                <p className="text-sm leading-relaxed" style={{ fontFamily: "'Lora', serif", color: '#3A3A3A' }}>{item.d}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Contact */}
      <section className="bg-[#FAF9F6] py-16 border-t border-[#1A1A1A]/8">
        <div className="max-w-[600px] mx-auto px-6 text-center">
          <h3 className="text-2xl mb-4" style={{ fontFamily: "'Cormorant Garamond', serif", fontWeight: 400, color: '#1A1A1A' }}>Questions?</h3>
          <p className="text-base" style={{ fontFamily: "'Lora', serif", color: '#3A3A3A' }}>
            Contact us at <a href="mailto:support@shebuiltthis.com" className="text-[#C25E3E] hover:underline">support@shebuiltthis.com</a>
          </p>
        </div>
      </section>
    </div>
  );
};

export default ParticipatePage;
